// <SbIcon name="plug" size={20} /> — the React equivalent of the design
// system's <sb-icon> web component. Icons are the SVG files exported from
// the claude.ai/design project (design/icons/*.svg), inlined at build time
// via Vite's raw glob so they inherit `currentColor` from the surrounding
// text like the original component did.

import type { CSSProperties } from 'react';

const rawIcons = import.meta.glob('./icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/** name → svg markup, keyed by the icon's basename (e.g. "chevron-down"). */
const icons = new Map<string, string>(
  Object.entries(rawIcons).map(([path, svg]) => {
    const name = path.replace('./icons/', '').replace('.svg', '');
    return [name, svg];
  }),
);

/** All bundled icon names (design/icons/*.svg basenames). */
export type SbIconName = string;

export interface SbIconProps {
  /** Icon basename, e.g. `"sparkles"` — must exist in design/icons/. */
  readonly name: SbIconName;
  /** Square size in px (default 20, matching the web component). */
  readonly size?: number;
  readonly className?: string;
  readonly style?: CSSProperties;
  /** Accessible label; icons are decorative (aria-hidden) when omitted. */
  readonly label?: string;
}

export function SbIcon(props: SbIconProps): React.ReactElement | null {
  const svg = icons.get(props.name);
  if (svg === undefined) {
    // An unknown name is a programming error; render nothing rather than
    // throwing mid-page, but make it visible in dev consoles.
    if (import.meta.env.DEV) {
      console.warn(`SbIcon: unknown icon name "${props.name}"`);
    }
    return null;
  }
  const size = props.size ?? 20;
  return (
    <span
      className={props.className === undefined ? 'sb-icon' : `sb-icon ${props.className}`}
      style={{ width: size, height: size, ...props.style }}
      aria-hidden={props.label === undefined ? true : undefined}
      aria-label={props.label}
      role={props.label === undefined ? undefined : 'img'}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted static assets bundled from design/icons/*.svg at build time
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
